import { useCallback, useEffect, useRef, useState } from 'react'
import type { Direction } from '@/types/game'
import type { LevelMeta } from '@/game/levels'
import { computeScore, computeStars, type RunOutcome } from '@/game/scoring'
import type { StarCount } from '@/types/save'

export interface LevelRunState {
  slides: number
  coinsCollected: number
  timeMs: number
  running: boolean
}

export interface ResolvedRun extends RunOutcome {
  stars: StarCount
  score: number
}

export function useLevelRun(level: LevelMeta) {
  const [state, setState] = useState<LevelRunState>({ slides: 0, coinsCollected: 0, timeMs: 0, running: true })
  const startRef = useRef<number>(Date.now())
  const pausedAtRef = useRef<number | null>(null)
  const timerRef = useRef<number>(0)

  useEffect(() => {
    startRef.current = Date.now()
    setState({ slides: 0, coinsCollected: 0, timeMs: 0, running: true })
  }, [level.index])

  useEffect(() => {
    const tick = () => {
      if (pausedAtRef.current == null) {
        setState((s) => (s.running ? { ...s, timeMs: Date.now() - startRef.current } : s))
      }
    }
    timerRef.current = window.setInterval(tick, 100)
    return () => window.clearInterval(timerRef.current)
  }, [])

  const registerSlide = useCallback((_dir: Direction) => {
    setState((s) => (s.running ? { ...s, slides: s.slides + 1 } : s))
  }, [])

  const totalCollectibles = level.coinCount + level.gemCount
  const collectCoin = useCallback(() => {
    setState((s) => ({ ...s, coinsCollected: Math.min(totalCollectibles, s.coinsCollected + 1) }))
  }, [totalCollectibles])

  const pause = useCallback(() => {
    if (pausedAtRef.current == null) pausedAtRef.current = Date.now()
  }, [])

  const resume = useCallback(() => {
    if (pausedAtRef.current != null) {
      startRef.current += Date.now() - pausedAtRef.current
      pausedAtRef.current = null
    }
  }, [])

  const reset = useCallback(() => {
    startRef.current = Date.now()
    pausedAtRef.current = null
    setState({ slides: 0, coinsCollected: 0, timeMs: 0, running: true })
  }, [])

  const resolve = useCallback(
    (reachedHome: boolean, scoreMult = 1): ResolvedRun => {
      const finalTimeMs = pausedAtRef.current == null ? Date.now() - startRef.current : pausedAtRef.current - startRef.current
      const outcome: RunOutcome = {
        reachedHome,
        slides: state.slides,
        coinsCollected: state.coinsCollected,
        timeMs: state.running ? finalTimeMs : state.timeMs,
      }
      const stars = computeStars(level, outcome)
      const score = Math.round(computeScore(level, outcome, stars) * Math.max(1, scoreMult))
      setState((s) => ({ ...s, running: false }))
      return { ...outcome, stars, score }
    },
    [level, state.slides, state.coinsCollected, state.timeMs],
  )

  return { state, registerSlide, collectCoin, pause, resume, reset, resolve }
}
